/**
 * Main Article Section JavaScript
 * Handles tooltips and infinite scroll functionality
 */

class InfiniteScrollArticles {
  constructor(container) {
    console.log('[InfiniteScroll] Initializing InfiniteScrollArticles');
    console.log('[InfiniteScroll] Container:', container);

    this.container = container;
    this.trigger = container.querySelector('.infinite-scroll-trigger');
    this.content = container.querySelector('.infinite-scroll-content');
    this.loading = container.querySelector('.infinite-scroll-loading');

    console.log('[InfiniteScroll] Found elements:', {
      trigger: this.trigger,
      content: this.content,
      loading: this.loading
    });

    // Data attributes
    this.blogHandle = container.dataset.blogHandle;
    this.currentArticleId = container.dataset.currentArticleId;
    this.currentTags = container.dataset.currentTags ? container.dataset.currentTags.split(',') : [];
    this.perPage = 1; // Always load 1 article at a time for full content

    console.log('[InfiniteScroll] Configuration:', {
      blogHandle: this.blogHandle,
      currentArticleId: this.currentArticleId,
      currentTags: this.currentTags,
      perPage: this.perPage
    });

    // State
    this.isLoading = false;
    this.hasMoreContent = true;
    this.page = 1;
    this.loadedArticleIds = new Set([this.currentArticleId]);
    this.lastTriggerTime = 0; // Prevent rapid multiple triggers

    this.init();
  }

  init() {
    console.log('[InfiniteScroll] 🎯 Initializing infinite scroll functionality');

    // Add a manual trigger for testing
    if (window.location.hash === '#debug-infinite-scroll') {
      this.addDebugButton();
    }

    setTimeout(() => {
      this.setupScrollWatcher();
      this.setupScrollSync();
    }, 1000); // Delay to ensure all elements are loaded
  }

  addDebugButton() {
    console.log('[InfiniteScroll] 🧪 Adding debug button');
    const debugButton = document.createElement('button');
    debugButton.textContent = 'Debug: Load More Articles';
    debugButton.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 9999;
      background: #007bff;
      color: white;
      border: none;
      padding: 10px;
      cursor: pointer;
      border-radius: 5px;
    `;
    debugButton.addEventListener('click', () => {
      console.log('[InfiniteScroll] 🧪 Debug button clicked');
      this.loadNextArticle();
    });
    document.body.appendChild(debugButton);
  }

  setupScrollSync() {
    console.log("[InfiniteScroll] 🛰️ Setting up scroll sync for handle-based URL");

    let lastScrollY = window.scrollY;

    const observer = new IntersectionObserver(
      (entries) => {
        const currentScrollY = window.scrollY;
        const isScrollingDown = currentScrollY > lastScrollY;
        lastScrollY = currentScrollY;

        entries.forEach((entry) => {
          const separator = entry.target;
          const nextHandle = separator.dataset.nextHandle;
          const prevHandle = separator.dataset.prevHandle;
          const blogPath = this.blogHandle;

          if (entry.isIntersecting) {
            const newHandle = isScrollingDown ? nextHandle : prevHandle;
            if (newHandle) {
              const newPath = `/blogs/${newHandle}`;
              history.replaceState({}, "", newPath);
              console.log(`[InfiniteScroll] ${isScrollingDown ? "⬇️ Down" : "⬆️ Up"} — Updated path:`, newPath);
              
              // Dispatch page view event when URL changes
              this.dispatchPageViewEvent({
                url: newPath,
                title: separator.dataset.articleTitle || document.title,
                articleId: separator.dataset.articleId,
                articleHandle: newHandle,
                trigger: 'scroll'
              });
            }
          }
        });
      },
      {
        root: null,
        threshold: 0.5,
      }
    );

    const observeExistingArticles = () => {
      const allSeparators = document.querySelectorAll('.infinite-scroll-separator');
      allSeparators.forEach(separator => observer.observe(separator));
      console.log("[InfiniteScroll] 👀 Observing separators:", allSeparators.length);
    };

    observeExistingArticles();

    this.scrollObserver = observer;
    this.observeExistingArticles = observeExistingArticles;
  }

  setupScrollWatcher() {
    console.log('[InfiniteScroll] 🔄 Setting up scroll watcher (timeout-based)');
    console.log('[InfiniteScroll] Trigger element:', this.trigger);
    console.log('[InfiniteScroll] Content element:', this.content);

    if (!this.trigger) {
      console.error('[InfiniteScroll] No trigger element found! Cannot setup scroll watcher.');
      return;
    }

    if (!this.content) {
      console.error('[InfiniteScroll] No content element found! Cannot setup scroll watcher.');
      return;
    }

    // Start the scroll checking interval
    this.scrollCheckInterval = setInterval(() => {
      this.checkTriggerVisibility();
    }, 400);

    console.log('[InfiniteScroll] ✅ Scroll watcher started - checking every 400ms');

    // Initial position log
    this.logTriggerPosition();
  }

  checkTriggerVisibility() {
    if (!this.trigger || this.isLoading || !this.hasMoreContent) {
      return;
    }

    const triggerRect = this.trigger.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const isVisible = triggerRect.top <= windowHeight && triggerRect.bottom >= 0;

    if (isVisible) {
      const now = Date.now();
      const timeSinceLastTrigger = now - this.lastTriggerTime;

      console.log('[InfiniteScroll] ⏰ Trigger visible - timing check:', {
        timeSinceLastTrigger: timeSinceLastTrigger,
        minInterval: 2000
      });

      if (timeSinceLastTrigger > 2000) {
        console.log('[InfiniteScroll] ✅ Loading next article!');
        this.lastTriggerTime = now;
        this.loadNextArticle();
      } else {
        console.log('[InfiniteScroll] ⏳ Still in cooldown period');
      }
    }
  }

  logTriggerPosition() {
    if (this.trigger) {
      const triggerRect = this.trigger.getBoundingClientRect();
      console.log('[InfiniteScroll] 📍 Initial trigger position:', {
        top: Math.round(triggerRect.top),
        bottom: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left),
        right: Math.round(triggerRect.right),
        height: Math.round(triggerRect.height),
        width: Math.round(triggerRect.width)
      });
    }
  }

  async loadNextArticle() {
    console.log('[InfiniteScroll] 🔄 loadNextArticle called');
    console.log('[InfiniteScroll] Current state check:', {
      isLoading: this.isLoading,
      hasMoreContent: this.hasMoreContent,
      page: this.page
    });

    if (this.isLoading || !this.hasMoreContent) {
      console.log('[InfiniteScroll] ⚠️ Early return - already loading or no more content');
      return;
    }

    console.log('[InfiniteScroll] 🚀 Starting to load next article...');
    this.isLoading = true;
    this.showLoading();

    try {
      console.log('[InfiniteScroll] 📡 Fetching next article for page:', this.page);
      const nextArticle = await this.fetchNextArticle();

      console.log('[InfiniteScroll] 📦 Fetched article:', {
        found: nextArticle ? true : false,
        article: nextArticle ? { id: nextArticle.id, title: nextArticle.title, handle: nextArticle.handle } : null
      });

      if (!nextArticle) {
        console.log('[InfiniteScroll] 🏁 No more articles found - ending infinite scroll');
        this.hasMoreContent = false;
        this.hideLoading();
        this.showEndMessage();
        return;
      }

      console.log('[InfiniteScroll] 🎨 Rendering next article...');
      await this.renderNextArticle(nextArticle);
      this.page++;
      console.log('[InfiniteScroll] ✅ Successfully loaded article, page now:', this.page);

      // console.log('nextArticle is Here:', nextArticle);
      // --- ADD POSTHOG CAPTURE HERE ---
      if (window.posthog) {
        window.posthog.capture('news:blog_post_view', {
          'article_title': nextArticle.title,
          'article_handle': nextArticle.handle,
          'author': nextArticle.author,
          'url': nextArticle.url,
          'tags': nextArticle.tags ? nextArticle.tags.join(', ') : '',
          'trigger': 'infinite_scroll',
          'site_platform': 'News'
        });
        // console.log('[PostHog] ✅ Infinite scroll view captured:', nextArticle.title);
      }
      // --------------------------------

      // Dispatch page view event after article is successfully loaded
      this.dispatchPageViewEvent({
        url: nextArticle.url,
        title: nextArticle.title,
        articleId: nextArticle.id,
        articleHandle: nextArticle.handle,
        trigger: 'autoload'
      });

    } catch (error) {
      console.error('[InfiniteScroll] ❌ Error loading next article:', error);
      this.showError();
    } finally {
      console.log('[InfiniteScroll] 🔄 Cleaning up loading state');
      this.isLoading = false;
      this.hideLoading();
    }
  }

  /**
   * Dispatch custom page view event for analytics and ad reloading
   * @param {Object} data - Page view data
   */
  dispatchPageViewEvent(data) {

    if (data.title) {
      document.title = data.title; 
      console.log('[InfiniteScroll] 🏷️ Browser title updated to:', data.title);
    }
    
    const event = new CustomEvent('page:view:custom', {
      bubbles: true,
      detail: {
        url: data.url,
        path: data.url,
        title: data.title,
        articleId: data.articleId,
        articleHandle: data.articleHandle,
        timestamp: new Date().toISOString(),
        trigger: data.trigger, // 'autoload' or 'scroll'
        blogHandle: this.blogHandle
      }
    });

    document.dispatchEvent(event);
    
    console.log('[InfiniteScroll] Dispatched page:view:custom event:', event.detail);
  }

  async fetchNextArticle() {
    // Construct URL for custom blog JSON endpoint - always fetch 1 article
    const url = `/blogs/${this.blogHandle}?view=json&limit=8&page=${this.page}`;
    console.log('[InfiniteScroll] Fetching next article from URL:', url);

    try {
      const response = await fetch(url);
      console.log('[InfiniteScroll] Response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('[InfiniteScroll] Raw API response:', {
        page: data.page,
        limit: data.limit,
        totalArticles: data.articles ? data.articles.length : 0,
        articles: data.articles ? data.articles.map(a => ({ id: a.id, title: a.title, tags: a.tags })) : []
      });

      // Check if we have articles in the response
      if (!data.articles || !Array.isArray(data.articles) || data.articles.length === 0) {
        console.log('[InfiniteScroll] No articles in response');
        return null;
      }

      const article = data.articles.find(a => !this.loadedArticleIds.has(a.id.toString()));

      if (!article) {
        console.log('[InfiniteScroll] All fetched articles have already been loaded');
        return null;
      }

      console.log('[InfiniteScroll] Found next article:', {
        id: article.id,
        title: article.title,
        handle: article.handle
      });

      // Add to loaded set
      this.loadedArticleIds.add(article.id.toString());
      console.log('[InfiniteScroll] Updated loaded article IDs:', Array.from(this.loadedArticleIds));

      return article;

    } catch (error) {
      console.error('[InfiniteScroll] Error fetching next article:', error);
      return null;
    }
  }

  async renderNextArticle(article) {
    console.log('[InfiniteScroll] Rendering article:', article.title);

    const articleElement = await this.createArticleElement(article);

    // Find the infinite scroll container and inject the article outside of it
    const infiniteScrollContainer = this.container;
    const parentContainer = infiniteScrollContainer.closest('main');

    if (parentContainer) {
      parentContainer.appendChild(articleElement);
      console.log('[InfiniteScroll] Appended article to parent container');
      this.reExecuteScripts(articleElement);
      // Initialize any slideshows in the newly injected content
      this.initializeSlideshows(articleElement);

      // Move the infinite scroll container after the new article
      parentContainer.appendChild(infiniteScrollContainer);
      console.log('[InfiniteScroll] Moved infinite scroll container after new article');

      // Re-observe the new article for scroll sync
      if (this.observeExistingArticles) {
        this.observeExistingArticles(); // Re-observe new article for scroll sync
      }
    }
  }

  reExecuteScripts(articleElement) {
    console.log('[InfiniteScroll] Re-executing ad scripts in injected article');
    const scripts = articleElement.querySelectorAll('script');
    let count = 0;

    scripts.forEach(oldScript => {
      // Skip external scripts (they're already loaded globally)
      if (oldScript.src) return;

      // Skip scripts that contain class declarations (theme JS)
      if (oldScript.textContent.includes('class InfiniteScrollArticles') ||
          oldScript.textContent.includes('class ArticleGallery') ||
          oldScript.textContent.includes('class ArticleTooltips') ||
          oldScript.textContent.includes('class TooltipContent')) return;

      // Only re-execute GPT/ad scripts
      if (!oldScript.textContent.includes('googletag')) return;

      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
      count++;
    });

    console.log('[InfiniteScroll] ✅ Re-executed', count, 'ad scripts');
  }

  initializeSlideshows(articleElement) {
    console.log('[InfiniteScroll] 🎠 Initializing slideshows in injected content');

    // Find swiper-container elements (this theme's slideshow implementation)
    const swiperContainers = articleElement.querySelectorAll('swiper-container');

    console.log('[InfiniteScroll] 📊 Found swiper-container elements:', swiperContainers.length);

    if (swiperContainers.length > 0) {
      // Method 1: Try to call the theme's initializeSwipers method if available
      if (window.swiperUtility && window.swiperUtility.initializeSwipers) {
        console.log('[InfiniteScroll] 🚀 Calling theme initializeSwipers method');
        window.swiperUtility.initializeSwipers();
      } else {
        // Method 2: Initialize each swiper-container manually
        swiperContainers.forEach((container, index) => {
          console.log(`[InfiniteScroll] 🔄 Initializing swiper-container ${index + 1}`);

          try {
            // Check if already initialized
            if (container.swiper) {
              console.log(`[InfiniteScroll] ⚠️ Swiper ${index + 1} already initialized, skipping`);
              return;
            }

            // Add slider-enabled class to the section
            const sectionElement = container.closest('.shopify-section, [class*="slideshow"]');
            if (sectionElement) {
              sectionElement.classList.add('slider-enabled');
              console.log(`[InfiniteScroll] ✅ Added slider-enabled class to section for swiper ${index + 1}`);
            }

            // Set up configuration based on data attributes
            const mobileSlidesPerView = parseFloat(container.dataset.mobileSlidesPerView) || 'auto';
            const desktopSlidesPerView = parseFloat(container.dataset.desktopSlidesPerView) || 'auto';
            const spaceBetweenMobile = Number(container.dataset.spaceBetweenMobile) || 16;
            const spaceBetweenDesktop = Number(container.dataset.spaceBetweenDesktop) || 32;

            const config = {
              breakpoints: {
                0: {
                  slidesPerView: mobileSlidesPerView,
                  spaceBetween: spaceBetweenMobile
                },
                1024: {
                  slidesPerView: desktopSlidesPerView,
                  spaceBetween: spaceBetweenDesktop
                }
              }
            };

            // Apply configuration and initialize
            Object.assign(container, config);

            // Initialize the swiper
            if (container.initialize) {
              container.initialize();
              console.log(`[InfiniteScroll] ✅ Swiper ${index + 1} initialized successfully`);
            } else {
              console.log(`[InfiniteScroll] ⚠️ Swiper ${index + 1} has no initialize method`);
            }

          } catch (error) {
            console.error(`[InfiniteScroll] ❌ Error initializing swiper ${index + 1}:`, error);
          }
        });
      }

      // Method 3: Trigger a general section load event to reinitialize everything
      setTimeout(() => {
        console.log('[InfiniteScroll] 📡 Triggering shopify:section:load event');
        const sectionLoadEvent = new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: 'dynamically-loaded-article' }
        });
        document.dispatchEvent(sectionLoadEvent);
      }, 100);
    }

    console.log('[InfiniteScroll] ✅ Slideshow initialization complete');
  }

  async createArticleElement(article) {
    console.log('[InfiniteScroll] 🎨 Fetching full article content for:', article.title);
    console.log('[InfiniteScroll] 📍 Article URL:', article.url);

    try {
      // Fetch the full article page HTML
      const response = await fetch(article.url);

      if (!response.ok) {
        throw new Error(`Failed to fetch article: ${response.status}`);
      }

      const html = await response.text();
      console.log('[InfiniteScroll] 📄 Fetched article HTML, length:', html.length);

      // Parse the HTML to extract the main content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the main content area
      const mainContent = doc.querySelector('#main-content');

      if (!mainContent) {
        console.error('[InfiniteScroll] ❌ Could not find #main-content in fetched HTML');
        throw new Error('Could not extract article content');
      }

      console.log('[InfiniteScroll] ✅ Successfully found #main-content');

      // Create a new container for the article content
      const articleSection = document.createElement('div');
      articleSection.className = 'infinite-scroll-article-section';
      articleSection.dataset.articleId = article.id;

      // Add a separator before the article
      const separator = document.createElement('div');
      separator.className = 'infinite-scroll-separator';
      separator.style.cssText = 'margin: 60px 0 40px 0; border-top: 2px solid #e0e0e0; position: relative;';

      separator.dataset.articleId = article.id;
      separator.dataset.articleHandle = article.handle;
      separator.dataset.articleTitle = article.title;

      // Use previously loaded handle as prevHandle
      separator.dataset.prevHandle = this.lastArticleHandle || `${this.blogHandle}/${window.location.pathname.split('/').pop()}`;
      // Current article as the nextHandle
      separator.dataset.nextHandle = article.handle;

      // Save current for the next loop
      this.lastArticleHandle = article.handle;

      const separatorLabel = document.createElement('div');
      separatorLabel.className = 'infinite-scroll-separator-label';
      separatorLabel.textContent = `Continue Reading: ${article.title}`;
      separatorLabel.style.cssText = `
        position: absolute;
        top: -12px;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        padding: 0 20px;
        color: #666;
        font-size: 14px;
        font-weight: 500;
      `;
      separator.appendChild(separatorLabel);

      articleSection.appendChild(separator);

      // Clone only the innerHTML of main-content (not the container itself)
      Array.from(mainContent.children).forEach(child => {
        const clonedChild = child.cloneNode(true);

        // Remove infinite scroll elements, related articles, and comments from each child
        const elementsToRemove = clonedChild.querySelectorAll?.('.infinite-scroll-container, .infinite-scroll-trigger, .infinite-scroll-content, .infinite-scroll-loading, .related-articles, [id*="main_comments"]') || [];
        elementsToRemove.forEach(element => {
          console.log('[InfiniteScroll] 🗑️ Removing duplicate element:', element.className || element.id);
          element.remove();
        });

        // Also check if the cloned child itself matches our removal criteria
        const shouldRemoveChild =
          clonedChild.classList?.contains('infinite-scroll-container') ||
          clonedChild.classList?.contains('infinite-scroll-trigger') ||
          clonedChild.classList?.contains('infinite-scroll-content') ||
          clonedChild.classList?.contains('infinite-scroll-loading') ||
          clonedChild.classList?.contains('related-articles') ||
          clonedChild.id?.includes('main_comments');

        if (!shouldRemoveChild) {
          articleSection.appendChild(clonedChild);
        } else {
          console.log('[InfiniteScroll] 🗑️ Skipping entire child element:', clonedChild.className || clonedChild.id);
        }
      });

      console.log('[InfiniteScroll] 🧹 Cleaned up and extracted content from #main-content');
      console.log('[InfiniteScroll] ✅ Created article section for:', article.title);

      document.dispatchEvent(new CustomEvent('article:load'));
      return articleSection;

    } catch (error) {
      console.error('[InfiniteScroll] ❌ Error creating article element:', error);

      // Fallback: create a simple error message
      const errorDiv = document.createElement('div');
      errorDiv.className = 'infinite-scroll-error-article';
      errorDiv.innerHTML = `
        <div style="padding: 40px; text-align: center; color: #666;">
          <p>Could not load article: <strong>${article.title}</strong></p>
          <a href="${article.url}" style="color: #007bff;">View article directly →</a>
        </div>
      `;
      return errorDiv;
    }
  }

  createExcerpt(content, maxLength = 150) {
    if (!content) return '';

    // Strip HTML tags
    const textContent = content.replace(/<[^>]*>/g, '');

    if (textContent.length <= maxLength) {
      return textContent;
    }

    // Truncate and add ellipsis
    return textContent.substr(0, maxLength).trim() + '...';
  }

  showLoading() {
    this.loading.style.display = 'block';
  }

  hideLoading() {
    this.loading.style.display = 'none';
  }

  showEndMessage() {
    if (!this.container.querySelector('.infinite-scroll-end')) {
      const endMessage = document.createElement('div');
      endMessage.className = 'infinite-scroll-end';
      endMessage.innerHTML = '<p>No more articles to load.</p>';
      this.content.appendChild(endMessage);
    }
  }

  showError() {
    if (!this.container.querySelector('.infinite-scroll-error')) {
      const errorMessage = document.createElement('div');
      errorMessage.className = 'infinite-scroll-error';
      errorMessage.innerHTML = `
        <p>Error loading articles. <button class="retry-btn">Try again</button></p>
      `;

      const retryBtn = errorMessage.querySelector('.retry-btn');
      retryBtn.addEventListener('click', () => {
        errorMessage.remove();
        this.loadNextArticle();
      });

      this.content.appendChild(errorMessage);
    }
  }

  destroy() {
    console.log('[InfiniteScroll] 🧹 Cleaning up scroll watcher');
    if (this.scrollCheckInterval) {
      clearInterval(this.scrollCheckInterval);
      this.scrollCheckInterval = null;
    }
  }
}

// Tooltip functionality (existing)
class ArticleTooltips {
  constructor() {
    this.init();
  }

  init() {
    if (Shopify.designMode) return;

    const showEvents = ['mouseenter', 'focus'];
    const hideEvents = ['mouseleave', 'blur'];

    document.querySelectorAll('.main-article a').forEach(link => {
      const href = link.getAttribute('href');

      if (href && href.indexOf(window.routes.shop_domain + '/products/') > -1) {
        const productHandle = href.split('/products/')[1].replace('/', '');
        const tooltip = document.getElementById(productHandle);

        if (tooltip) {
          showEvents.forEach(event => {
            link.addEventListener(event, e => tooltip.show(e.target));
          });

          hideEvents.forEach(event => {
            link.addEventListener(event, e => {
              if (!e.relatedTarget.closest(`tooltip-content#${tooltip.id}`)) {
                tooltip.hide();
              }
            });
          });
        }
      }
    });
  }
}

// Tooltip Content Custom Element (existing)
if (!Shopify.designMode) {
  class TooltipContent extends HTMLElement {
    constructor() {
      super();
      this.show = this.show.bind(this);
      this.hide = this.hide.bind(this);
    }

    show(trigger) {
      this.updatePosition(trigger);
      this.setAttribute('data-show', '');
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
    }

    async updatePosition(trigger) {
      // Using floating-ui for positioning (assuming it's available)
      if (window.FloatingUICore) {
        const { computePosition, flip, shift } = window.FloatingUICore;

        const { x, y } = await computePosition(trigger, this, {
          placement: 'top',
          middleware: [flip(), shift()]
        });

        Object.assign(this.style, {
          left: `${x}px`,
          top: `${y}px`
        });
      }
    }

    hide() {
      this.removeAttribute('data-show');
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
    }

    handleMouseEnter() {
      this.classList.add('tooltip--hovered');
    }

    handleMouseLeave() {
      this.classList.remove('tooltip--hovered');
      this.hide();
    }
  }

  if (!customElements.get('tooltip-content')) {
    customElements.define('tooltip-content', TooltipContent);
  }
}

/**
 * Makes all iframes within the article body responsive by:
 * 1. Stripping hardcoded width/height attributes
 * 2. Wrapping them in a responsive 16:9 container
 * 3. Cleaning up malformed HTML text fragments
 */
function makeArticleIframesResponsive(container) {
  if (!container) return;

  // Clean up malformed HTML text fragments that appear as visible text.
  // These come from broken embed code in the rich text editor, e.g.:
  //   " width="680">"   or   " >"   or   " rel="noreferrer noopener" target="_blank">
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
  const nodesToClean = [];
  while (walker.nextNode()) {
    const trimmed = walker.currentNode.textContent.trim();
    // Match stray attribute fragments: starts with " and contains HTML-attribute-like patterns ending with >
    if (/^["']\s*(>|width|height|rel|target|class|style|data-)/.test(trimmed) && trimmed.indexOf('>') !== -1) {
      // Only remove if the text node is inside a <p> or directly in the wysiwyg container,
      // and doesn't contain meaningful content beyond the fragment
      const parentTag = walker.currentNode.parentElement?.tagName;
      const textAfterClose = trimmed.substring(trimmed.indexOf('>') + 1).trim();
      // If there's meaningful text after the >, it's likely a broken link — remove just the attribute fragment
      if (textAfterClose.length > 0) {
        // Keep the meaningful text, strip the broken attribute prefix
        walker.currentNode.textContent = textAfterClose;
      } else if (parentTag === 'P' || parentTag === 'DIV' || parentTag === 'SPAN') {
        nodesToClean.push(walker.currentNode);
      }
    }
  }
  nodesToClean.forEach(node => {
    // If removing leaves the parent empty, remove the parent too
    const parent = node.parentElement;
    node.remove();
    if (parent && parent.innerHTML.trim() === '') {
      parent.remove();
    }
  });

  // Find all iframes and make them responsive
  const iframes = container.querySelectorAll('iframe');
  iframes.forEach(iframe => {
    // Skip if already wrapped
    if (iframe.parentElement && iframe.parentElement.classList.contains('responsive-iframe-wrapper')) return;

    // Remove hardcoded width/height attributes
    iframe.removeAttribute('width');
    iframe.removeAttribute('height');

    // Create responsive wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'responsive-iframe-wrapper';

    // If iframe is inside a <p> tag, move the wrapper outside the <p>
    const parent = iframe.parentElement;
    if (parent && parent.tagName === 'P') {
      parent.parentNode.insertBefore(wrapper, parent);
      wrapper.appendChild(iframe);
      // Remove the <p> if it's now empty
      if (parent.innerHTML.trim() === '') {
        parent.remove();
      }
    } else {
      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);
    }
  });
}

// Initialize everything when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Make article iframes responsive
  makeArticleIframesResponsive(document.querySelector('.main-article.wysiwyg'));

  // Initialize tooltips
  new ArticleTooltips();

  // Initialize infinite scroll
  const infiniteScrollContainer = document.querySelector('.infinite-scroll-container');
  if (infiniteScrollContainer) {
    new InfiniteScrollArticles(infiniteScrollContainer);
  }

  // Handle slideshow relocation for article galleries
  setTimeout(() => {
    relocateBasicSlideshow();
  }, 100);
});

// Also handle dynamically loaded articles (infinite scroll)
document.addEventListener('article:load', () => {
  document.querySelectorAll('.main-article.wysiwyg, .infinite-scroll-article-section .wysiwyg').forEach(container => {
    makeArticleIframesResponsive(container);
  });
});

// Function to relocate basic-slideshow to article-gallery if present
function relocateBasicSlideshow() {
  return; // Disabled for now
  const articleGallery = document.querySelector('#article-gallery');
  const basicSlideshow = document.querySelector('.basic-slideshow');

  if (basicSlideshow && articleGallery) {
    console.log('[Gallery] Moving basic-slideshow to article-gallery location');
    const slideshowParent = basicSlideshow.closest('.shopify-section');

    // Remove the slideshow from its current location and move to article-gallery
    if (slideshowParent) {
      slideshowParent.remove();
    } else {
      basicSlideshow.remove();
    }

    // Insert the slideshow into the article-gallery div
    if (slideshowParent) {
      articleGallery.appendChild(slideshowParent);
    } else {
      articleGallery.appendChild(basicSlideshow);
    }

    document.dispatchEvent(new CustomEvent('article:load'));
    console.log('[Gallery] Successfully moved basic-slideshow to article-gallery');
  } else if (basicSlideshow && !articleGallery) {
    console.log('[Gallery] basic-slideshow found but no article-gallery, leaving slideshow at top');
  } else if (!basicSlideshow) {
    console.log('[Gallery] No basic-slideshow found on page');
  } else {
    console.log('[Gallery] article-gallery found but no basic-slideshow to move');
  }
}

// Handle theme editor updates
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    const infiniteScrollContainer = document.querySelector('.infinite-scroll-container');
    if (infiniteScrollContainer) {
      new InfiniteScrollArticles(infiniteScrollContainer);
    }

    // Also handle slideshow relocation when sections are loaded/updated
    relocateBasicSlideshow();
  });
}